import React, { useMemo, useState } from "react";
import {
    Badge,
    Button,
    Nav,
    Navbar,
    OverlayTrigger,
    Tooltip
} from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { useLocalState } from "../../graphql/hooks";
import { MeData, ProjectEnvironment, StateData } from "../../models/types";
import { GET_LOCAL_STATE, GET_ME } from "../../graphql/queries";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { UPDATE_ME } from "../../graphql/mutations";
import { NotificationsIcon } from "../simple/NotificationsIcon";
import { UserDropdown } from "../dropdowns/UserDropdown";
import { getEnvironment } from "../../common/Helpers";
import { useAuth0 } from "@auth0/auth0-react";
import { ProtectedComponent } from "../../auth/ProtectedComponent";
import { SeepianOnlyComponent } from "../../auth/SeepianOnly";
import { AdminPanelNavLink } from "../links/AdminPanelNavLink";
import seepiaPlatformLight from "../../assets/Seepia_black.png";
import seepiaPlatformDark from "../../assets/Seepia_white.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoon, faSun } from "@fortawesome/free-solid-svg-icons";
import styles from "./NavigationPanel.module.scss";

export const NavigationPanel = () => {
    const { logout } = useAuth0();
    const location = useLocation();
    const client = useApolloClient();
    const state = useLocalState();
    const [expanded, updateExpanded] = useState(false);
    const { data: { me } = {} } = useQuery<MeData>(GET_ME, {
        fetchPolicy: "cache-only"
    });
    const [updateMe] = useMutation(UPDATE_ME);

    const updateDeveloperState = (isDeveloperMode: boolean) => {
        client.writeQuery<StateData, StateData>({
            query: GET_LOCAL_STATE,
            data: { state: { ...state, developerMode: isDeveloperMode } }
        });
        if (!isDeveloperMode && me?.testerMode) {
            updateTesterState(false);
        }
    };

    const updateTesterState = async (testerMode: boolean) => {
        try {
            await updateMe({
                variables: { testerMode: testerMode }
            });
        } catch (error) {
            console.log("[DEBUG] updateTesterState error ", error);
        }
    };

    const updateDarkModeState = async (useDarkMode: boolean) => {
        try {
            await updateMe({
                variables: { useDarkMode: useDarkMode }
            });
            client.writeQuery<StateData, StateData>({
                query: GET_LOCAL_STATE,
                data: { state: { ...state, useDarkMode: useDarkMode } }
            });
        } catch (error) {
            console.log("[DEBUG] updateDarkModeState error ", error);
        }
    };

    const updatePictureUrl = async (pictureUrl: string) => {
        try {
            await updateMe({
                variables: { pictureUrl: pictureUrl }
            });
        } catch (error) {
            console.log("[DEBUG] updatePictureUrl error ", error);
        }
    };

    const logoutUser = async () => {
        localStorage.clear();
        logout();
    };

    const environment = getEnvironment();
    const environmentBadge = () => {
        switch (environment) {
            case ProjectEnvironment.Local: {
                return <Badge bg="danger">Local</Badge>;
            }
            case ProjectEnvironment.Development: {
                return <Badge bg="info">Development</Badge>;
            }
            case ProjectEnvironment.Production: {
                return null;
            }
            default:
                return <Badge bg="danger">Development</Badge>;
        }
    };

    const activeKey = useMemo(() => {
        const matches = location.pathname.match("[^/]+");
        return matches ? matches[0] : null;
    }, [location.pathname]);

    return (
        <Navbar
            variant={state.useDarkMode ? "dark" : "light"}
            expand="sm"
            fixed="top"
            onToggle={expanded => updateExpanded(expanded)}
            className={styles.navigationBarBottom}
            collapseOnSelect={true}
        >
            <Navbar.Brand as={Link} to="/home">
                <img
                    src={
                        state.useDarkMode
                            ? seepiaPlatformDark
                            : seepiaPlatformLight
                    }
                    alt="Seepia Platform"
                    height="30"
                />
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
                <Nav className="me-auto" activeKey={activeKey || undefined}>
                    <Nav.Link eventKey="products" as={Link} to="/products">
                        {state.developerMode ? "Products" : "Projects"}
                    </Nav.Link>
                    <Nav.Link eventKey="guides" as={Link} to="/guides">
                        Guides
                    </Nav.Link>
                    <Nav.Link eventKey="contact" as={Link} to="/contact">
                        Contact
                    </Nav.Link>
                    <SeepianOnlyComponent>
                        <Nav.Link
                            eventKey="buildStatistics"
                            as={Link}
                            to="/buildStatistics"
                        >
                            Statistics
                        </Nav.Link>
                    </SeepianOnlyComponent>
                    {state.developerMode && (
                        <>
                            <ProtectedComponent
                                scopes={[
                                    "admin",
                                    "organizations-admin",
                                    "organization-admin"
                                ]}
                            >
                                <AdminPanelNavLink />
                            </ProtectedComponent>
                            <Nav.Link>{environmentBadge()}</Nav.Link>
                        </>
                    )}
                </Nav>
                {me !== undefined ? (
                    <>
                        <Nav.Link
                            onClick={() =>
                                updateDarkModeState(!state.useDarkMode)
                            }
                            className="p-2 ms-3 me-2"
                        >
                            <OverlayTrigger
                                placement="bottom"
                                delay={{ show: 550, hide: 100 }}
                                overlay={
                                    <Tooltip id={`tooltip-bottom`}>
                                        Toggle{" "}
                                        {state.useDarkMode ? "Light" : "Dark"}{" "}
                                        Mode
                                    </Tooltip>
                                }
                            >
                                <div>
                                    <FontAwesomeIcon
                                        icon={
                                            state.useDarkMode ? faSun : faMoon
                                        }
                                        size="lg"
                                    />
                                </div>
                            </OverlayTrigger>
                        </Nav.Link>
                        <NotificationsIcon user={me} mobileMode={expanded} />
                        <UserDropdown
                            logoutUser={logoutUser}
                            state={state}
                            updateDeveloperState={updateDeveloperState}
                            updateTesterState={updateTesterState}
                            updatePictureUrl={updatePictureUrl}
                            me={me}
                        />
                    </>
                ) : (
                    <Button onClick={() => logoutUser()}>Logout</Button>
                )}
            </Navbar.Collapse>
        </Navbar>
    );
};
